import { computed, effect, inject, Injectable, linkedSignal } from '@angular/core';
import { FacilityLookup } from '../interfaces/lookup.interface';
import { HomeService } from './home.service';
import { UserService } from './user.service';

const SELECTED_FACILITY_KEY = 'selectedFacilityId';

@Injectable({
  providedIn: 'root'
})
export class FacilityService {

  private readonly homeService = inject(HomeService);

  private readonly userService = inject(UserService);

  private readonly rememberedId = readRememberedId();

  readonly facilities = computed<FacilityLookup[]>(() => {
    const all = this.homeService.facilities();
    const permitted = new Set(this.userService.facilityIds());
    const mine = all
      .filter(facility => facility.isActive && permitted.has(facility.id))
      .sort((a, b) => a.name.localeCompare(b.name));
    console.log('facilities: %d from lookup, %o permitted on profile, %d after intersect',
      all.length, [...permitted], mine.length);
    return mine;
  });

  readonly selectedId = linkedSignal<FacilityLookup[], number | null>({
    source: this.facilities,
    computation: (facilities, previous) => {
      const kept = facilities.find(facility => facility.id === previous?.value);
      if (kept) return kept.id;

      const remembered = facilities.find(facility => facility.id === this.rememberedId);
      return remembered?.id ?? facilities[0]?.id ?? null;
    }
  });

  readonly selected = computed(() =>
    this.facilities().find(facility => facility.id === this.selectedId()) ?? null);

  readonly hasChoice = computed(() => this.facilities().length > 1);

  constructor() {
    effect(() => {
      const id = this.selectedId();
      if (id !== null) window.localStorage.setItem(SELECTED_FACILITY_KEY, String(id));
    });
  }

  // Orders carry facilityKey rather than the id. With nothing selected - the lookup has not
  // resolved, or failed - the list is handed back untouched rather than emptied.
  narrow<T extends { facilityKey: string }>(orders: T[]): T[] {
    const facility = this.selected();
    return facility ? orders.filter(order => order.facilityKey === facility.facilityKey) : orders;
  }
}

function readRememberedId(): number | null {
  const stored = window.localStorage.getItem(SELECTED_FACILITY_KEY);
  if (!stored) return null;

  const parsed = Number.parseInt(stored, 10);
  return Number.isNaN(parsed) ? null : parsed;
}
